import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

/**
 * Native Google reCAPTCHA v3 Service
 *
 * Replaces ng-recaptcha with a native implementation that's compatible
 * with any Angular version.
 */
@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private siteKey: string;
  private loaded = false;
  private loadPromise: Promise<void> | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.siteKey = environment.recaptcha?.siteKey || '';
  }

  /**
   * Load the reCAPTCHA script dynamically
   */
  private loadScript(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }

    if (this.loaded && window.grecaptcha) {
      return Promise.resolve();
    }

    this.loadPromise = new Promise((resolve, reject) => {
      // Check if script is already loaded
      const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
      if (existingScript) {
        this.waitForGrecaptcha().then(resolve).catch(reject);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.waitForGrecaptcha().then(() => {
          this.loaded = true;
          resolve();
        }).catch(reject);
      };

      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Wait for grecaptcha to be ready
   */
  private waitForGrecaptcha(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('reCAPTCHA ready timeout'));
      }, 10000);

      const checkGrecaptcha = () => {
        if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
          window.grecaptcha.ready(() => {
            clearTimeout(timeout);
            resolve();
          });
        } else {
          setTimeout(checkGrecaptcha, 100);
        }
      };

      checkGrecaptcha();
    });
  }

  /**
   * Execute reCAPTCHA v3 and get a token
   * @param action The action name for analytics (e.g., 'login', 'signup')
   * @returns Promise with the reCAPTCHA token
   */
  async execute(action: string): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('reCAPTCHA not available in SSR');
      return '';
    }

    if (!this.siteKey) {
      console.warn('reCAPTCHA site key not configured');
      return '';
    }

    try {
      await this.loadScript();

      if (!window.grecaptcha) {
        throw new Error('grecaptcha not available');
      }

      const token = await window.grecaptcha.execute(this.siteKey, { action });
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return '';
    }
  }

  /**
   * Verify reCAPTCHA and store token for backend verification
   * @param action The action being performed
   */
  async verifyAndStore(action: string): Promise<void> {
    try {
      console.log(`Executing reCAPTCHA for action: ${action}`);

      const token = await this.execute(action);

      if (token) {
        console.log('reCAPTCHA token generated successfully');
        sessionStorage.setItem('recaptcha_token', token);
        sessionStorage.setItem('recaptcha_action', action);
      } else {
        console.warn('reCAPTCHA token generation failed');
      }
    } catch (error) {
      console.error('reCAPTCHA error:', error);
    }
  }

  /**
   * Get stored reCAPTCHA token
   */
  getStoredToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return sessionStorage.getItem('recaptcha_token');
  }

  /**
   * Clear stored reCAPTCHA token
   */
  clearStoredToken(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('recaptcha_token');
      sessionStorage.removeItem('recaptcha_action');
    }
  }
}
